<template>
	<div
		class="AudioAnnotations"
		:class="{ 'AudioAnnotations--alternateHandles': alternateHandles }"
		v-on:mouseenter="mouseEnter"
		v-on:mouseleave="mouseLeave"
		v-on:mousedown="mouseDown"
		v-on:touchstart="mouseDown"
		v-on:contextmenu="rightClick"
		tabindex="-1"
		:style="{ width: width + 'px', height: height + 'px'}"
	>
		<div
			v-if="showAnnotations"
			v-for="(anno, index) in annotations"
			:key="index"
			class="Annotation"
			:class="{ hidden: anno.deleted || anno.hidden }"
			:style="{
				top: computeDisplay(anno, 'top'),
				left: computeDisplay(anno, 'left'),
				bottom: computeDisplay(anno, 'bottom'),
				right: computeDisplay(anno, 'right'),
				borderColor: anno.color,
				color: anno.color,
			}"
		>
			<div
				v-if="showLabels || alternateHandles"
				class="Annotation-label"
				:data-annotation-handle="index + '-label'"
				v-on:mousedown="mouseDown"
				v-on:touchstart="mouseDown"
				v-on:contextmenu.prevent.stop="$emit('annotation-selected', index, $event)"
			>
				{{ index + 1 }}: {{ anno.label }}
			</div>
			<template v-if="enableAnnotating">
				<div
					class="Annotation-handle Annotation-handle--topLeft"
					:data-annotation-handle="index + '-topLeft'"
					v-on:mousedown="mouseDown"
					v-on:touchstart="mouseDown"
				></div>
				<div
					class="Annotation-handle Annotation-handle--topRight"
					:data-annotation-handle="index + '-topRight'"
					v-on:mousedown="mouseDown"
					v-on:touchstart="mouseDown"
				></div>
				<div
					class="Annotation-handle Annotation-handle--bottomLeft"
					:data-annotation-handle="index + '-bottomLeft'"
					v-on:mousedown="mouseDown"
					v-on:touchstart="mouseDown"
				></div>
				<div
					class="Annotation-handle Annotation-handle--bottomRight"
					:data-annotation-handle="index + '-bottomRight'"
					v-on:mousedown="mouseDown"
					v-on:touchstart="mouseDown"
				></div>
				<div
					v-if="!alternateHandles"
					class="Annotation-handle Annotation-handle--middle"
					:data-annotation-handle="index + '-middle'"
					v-on:mousedown="mouseDown"
					v-on:touchstart="mouseDown"
				>
					{{ index + 1 }}
				</div>
			</template>
		</div>
		<div v-if="crosshair.show" class="Annotation-crosshair--x" :style="{ top: crosshair.x * 100 + '%' }"></div>
		<div v-if="crosshair.show" class="Annotation-crosshair--y" :style="{ left: crosshair.y * 100 + '%' }"></div>
	</div>
</template>

<script>
export default {
	name: 'CloAudioAnnotations',
	data() {
		return {
			initialClick: {
				x: null,
				y: null,
				offset: null,
				zoom: null,
				sealed: null,
				handleTimeOffset: null,
			},
			crosshair: {
				show: false,
				x: 0,
				y: 0,
			},
			mouseHeldDown: false,
			handleHeldDown: null,
			annotations: [],
			annotationToSave: null,
		};
	},
	props: {
		initialAnnotations: {
			type: Array,
			default() {
				return [];
			},
		},
		activeAnnotation: {
			type: Object,
			default() {
				return {};
			},
		},
		enableAnnotating: {
			type: Boolean,
			default: false,
		},
		showLabels: {
			type: Boolean,
			default: false,
		},
		alternateHandles: {
			type: Boolean,
			default: false,
		},
		showAnnotations: {
			type: Boolean,
			default: true,
		},
		annotationVersion: {
			type: String,
			default: '1.0',
		},
		offset: {
			type: Number,
			default: 0,
		},
		totalOffset: {
			type: Number,
			default: 0,
		},
		zoom: {
			type: Number,
			default: 1,
		},
		width: {
			type: Number,
			default: 0,
		},
		height: {
			type: Number,
			default: 0,
		},
		maxFrequency: {
			type: Number,
			default: 10,
		},
		basePxPerSec: {
			type: Number,
			default: 250,
		},
		/**
		 * How to position a cloned box after right-click: use 'x' for audio annotations
		 */
		 clonePosition: {
			type: String,
			default: 'xy'
		},
	},
	mounted() {
		// set data from prop
		this.annotations = this.initialAnnotations;

		// maybe not a good idea to attach to document, but this makes it easier to draw at the edges
		document.addEventListener('mousemove', this.mouseMove);
		document.addEventListener('mouseup', this.mouseUp);
		document.addEventListener('touchmove', this.mouseMove);
		document.addEventListener('touchend', this.mouseUp);
		document.addEventListener('touchcancel', this.mouseUp);
	},
	beforeDestroy() {
		document.removeEventListener('mousemove', this.mouseMove);
		document.removeEventListener('mouseup', this.mouseUp);
		document.removeEventListener('touchmove', this.mouseMove);
		document.removeEventListener('touchend', this.mouseUp);
		document.removeEventListener('touchcancel', this.mouseUp);
	},
	watch: {
		offset() {
			// TODO: add code for updating boxes in necessary
		}
	},
	methods: {
		mouseEnter(e) {
			if (this.enableAnnotating) {
				this.crosshair.show = true;
			}
		},
		mouseLeave(e) {
			if (this.enableAnnotating) {
				this.crosshair.show = false;
			}
		},
		mouseDown(e) {
			if (this.enableAnnotating && (e.which === 0 || e.which === 1)) {
				e.preventDefault();
				const el = this.$el.getBoundingClientRect();
				// save initial click position in percentage (between 0 and 1)
				let x = null;
				let y = null;
				if (e.which === 0) {
					// touch
					x = (e.touches[0].clientX - el.left) / el.width;
					y = (e.touches[0].clientY - el.top) / el.height;
				} else {
					// mouse
					x = (e.clientX - el.left) / el.width;
					y = (e.clientY - el.top) / el.height;
				}
				// Snaps the y coord to top or bottom if within 1%.
				// It makes starting a box near top or bottom easier.
				if (y > 0.99) {
					y = 1.0;
				}
				if (y < 0.01) {
					y = 0.0;
				}
				// ensure crosshairs are in a valid position
				if (this.xInBounds(x)) {
					let downOffset = this.offset;
					let downZoom = this.zoom;
					const handle = e.target.getAttribute('data-annotation-handle');
					if (!handle) {
						this.annotations.push(
							Object.assign(
								{
									coords: {
										startTime: this.convertProportionalXToSeconds(x, downOffset, downZoom),
										endTime: this.convertProportionalXToSeconds(x, downOffset, downZoom),
										minFreq: (1 - y) * this.maxFrequency,
										maxFreq: (1 - y) * this.maxFrequency,
									},
									new: true,
									edited: false,
									hidden: false,
									deleted: false,
								},
								this.activeAnnotation
							)
						);
						this.mouseHeldDown = true;
					} else {
						this.mouseHeldDown = false;
						this.handleHeldDown = handle;
						const handleInfo = handle.split('-')[1];
						if (handleInfo === 'label') {
							const i = parseInt(handle.split('-')[0]);
							this.initialClick.handleTimeOffset = this.convertProportionalXToSeconds(x, this.offset, this.zoom) -
							 									this.annotations[i].coords.startTime;
						}
					}
					this.initialClick.x = x;
					this.initialClick.y = y;
					this.initialClick.offset = downOffset;
					this.initialClick.zoom = downZoom;
					this.initialClick.sealed = true;
					this.$forceUpdate();
				}
			}
		},
		mouseUp(e) {
			if (this.enableAnnotating && (this.mouseHeldDown || this.handleHeldDown) && (e.which === 0 || e.which === 1)) {
				e.preventDefault();
				this.mouseHeldDown = false;
				this.handleHeldDown = false;
				this.annotations = this.annotations.filter((anno) => {
					return anno.coords.hasOwnProperty('startTime');
				});
				this.$forceUpdate();
				this.saveAnnotations();
			}
		},
		// show cursor on mouse hover
		mouseMove(e) {
			//console.log(e);
			if (this.enableAnnotating) {
				const el = this.$el.getBoundingClientRect();
				let x = null;
				let y = null;
				if (e.type === 'touchmove') {
					// touch
					x = (e.touches[0].clientX - el.left) / el.width;
					y = (e.touches[0].clientY - el.top) / el.height;
				} else {
					// mouse
					x = (e.clientX - el.left) / el.width;
					y = (e.clientY - el.top) / el.height;
				}
				this.crosshair.show = true;
				this.crosshair.x = y;
				this.crosshair.y = x;
				this.crosshair.show = (this.xInBounds(x) && y >= 0 && y <= 1);
				if (e.which === 0 || e.which === 1) {
					if (this.mouseHeldDown) {
						// dragging new annotation
						const i = this.annotations.length - 1;
						const currTime = this.convertProportionalXToSeconds(x, this.offset, this.zoom);
						const initialTime = this.convertProportionalXToSeconds(this.initialClick.x, this.initialClick.offset, this.initialClick.zoom);
						const currFreq = (1 - y) * this.maxFrequency;
						const initialFreq = (1 - this.initialClick.y) * this.maxFrequency;
						if (this.xInBounds(x)) {
							// dragging left or right
							this.annotations[i].coords.startTime = Math.min(initialTime, currTime);
							this.annotations[i].coords.endTime = Math.max(initialTime, currTime);
						}
						if (this.khzInBounds(currFreq)) {
							// dragging up or down
							this.annotations[i].coords.maxFreq = Math.max(initialFreq, currFreq);
							this.annotations[i].coords.minFreq = Math.min(initialFreq, currFreq);
						}
						this.annotationToSave = i;
					} else if (this.handleHeldDown) {
						// dragging existing annotation
						const handleInfo = this.handleHeldDown.split('-');
						const i = parseInt(handleInfo[0]);
						const handle = handleInfo[1];
						const currTime = this.convertProportionalXToSeconds(x, this.offset, this.zoom);
						const initialTime = this.convertProportionalXToSeconds(this.initialClick.x, this.initialClick.offset, this.zoom);
						const currFreq = (1 - y) * this.maxFrequency;
						const initialFreq = (1 - this.initialClick.y) * this.maxFrequency;
						const boxDuration = this.annotations[i].coords.endTime - this.annotations[i].coords.startTime
						const frequencyRange = this.annotations[i].coords.maxFreq - this.annotations[i].coords.minFreq;
						//console.log(x, y);
						switch (handle) {
							case 'middle':
								if ((currTime - initialTime) !== 0) {
									if (this.tInBounds(currTime - (boxDuration / 2)) && this.tInBounds(currTime + (boxDuration / 2))) {
										this.annotations[i].coords.startTime = currTime - (boxDuration / 2);
										this.annotations[i].coords.endTime = currTime + (boxDuration / 2);
									}
								}
								if ((currFreq - initialFreq) !== 0) {
									if (this.khzInBounds(currFreq + (frequencyRange / 2)) && this.khzInBounds(currFreq - (frequencyRange / 2))) {
										this.annotations[i].coords.maxFreq = currFreq + (frequencyRange / 2);
										this.annotations[i].coords.minFreq = currFreq - (frequencyRange / 2);
									}
								}
								break;
							case 'label':
								var newStartTime = currTime - this.initialClick.handleTimeOffset
								if ((currTime - initialTime) !== 0) {
									if (this.tInBounds(newStartTime) && this.tInBounds(newStartTime + boxDuration)) {
										this.annotations[i].coords.startTime = newStartTime;
										this.annotations[i].coords.endTime = newStartTime + boxDuration;
									}
									else if (newStartTime < 0) {
										this.annotations[i].coords.startTime = 0;
										this.annotations[i].coords.endTime = boxDuration;
									}
									else {
										var duration = (this.totalOffset * this.width) / (this.zoom * this.basePxPerSec);
										if (newStartTime + boxDuration > duration) {
											this.annotations[i].coords.startTime = duration - boxDuration;
											this.annotations[i].coords.endTime = duration;
										}
									}
								}
								if ((currFreq - initialFreq) !== 0) {
									var newMaxFreq = this.annotations[i].coords.maxFreq + (currFreq - initialFreq)
									if (this.khzInBounds(newMaxFreq) && this.khzInBounds(newMaxFreq - frequencyRange)) {
										this.annotations[i].coords.maxFreq = newMaxFreq;
										this.annotations[i].coords.minFreq = this.annotations[i].coords.maxFreq - frequencyRange;
									}
									else if (currFreq > this.maxFrequency) {
										this.annotations[i].coords.maxFreq = this.maxFrequency;
										this.annotations[i].coords.minFreq = this.maxFrequency - frequencyRange;
									}
									else if (newMaxFreq - frequencyRange < 0) {
										this.annotations[i].coords.maxFreq = frequencyRange;
										this.annotations[i].coords.minFreq = 0;
									}
								}
								break;
							case 'topLeft':
								if (this.tInBounds(currTime)) {
									if (currTime <= this.annotations[i].coords.endTime) {
										this.annotations[i].coords.startTime = currTime;
									}
									else {
										this.annotations[i].coords.startTime = this.annotations[i].coords.endTime;
										this.annotations[i].coords.endTime = currTime;
										this.handleHeldDown = (i + "-topRight");
									}
								}
								if (this.khzInBounds(currFreq)) {
									if (currFreq >= this.annotations[i].coords.minFreq) {
										this.annotations[i].coords.maxFreq = currFreq;
									}
									else {
										this.annotations[i].coords.maxFreq = this.annotations[i].coords.minFreq;
										this.annotations[i].coords.minFreq = currFreq;
										this.handleHeldDown = (i + "-bottomLeft");
									}
								}
								break;
							case 'topRight':
								if (this.tInBounds(currTime)) {
									if (currTime >= this.annotations[i].coords.startTime) {
										this.annotations[i].coords.endTime = currTime;
									}
									else {
										this.annotations[i].coords.endTime = this.annotations[i].coords.startTime;
										this.annotations[i].coords.startTime = currTime;
										this.handleHeldDown = (i + "-topLeft");
									}
								}
								if (this.khzInBounds(currFreq)) {
									if (currFreq >= this.annotations[i].coords.minFreq) {
										this.annotations[i].coords.maxFreq = currFreq;
									}
									else {
										this.annotations[i].coords.maxFreq = this.annotations[i].coords.minFreq;
										this.annotations[i].coords.minFreq = currFreq;
										this.handleHeldDown = (i + "-bottomRight");
									}
								}
								break;
							case 'bottomLeft':
								if (this.tInBounds(currTime)) {
									if (currTime <= this.annotations[i].coords.endTime) {
										this.annotations[i].coords.startTime = currTime;
									}
									else {
										this.annotations[i].coords.startTime = this.annotations[i].coords.endTime;
										this.annotations[i].coords.endTime = currTime;
										this.handleHeldDown = (i + "-bottomRight");
									}
								}
								if (this.khzInBounds(currFreq)) {
									if (currFreq <= this.annotations[i].coords.maxFreq) {
										this.annotations[i].coords.minFreq = currFreq;
									}
									else {
										this.annotations[i].coords.minFreq = this.annotations[i].coords.maxFreq;
										this.annotations[i].coords.maxFreq = currFreq;
										this.handleHeldDown = (i + "-topLeft");
									}
								}
								break;
							case 'bottomRight':
								if (this.tInBounds(currTime)) {
									if (currTime >= this.annotations[i].coords.startTime) {
										this.annotations[i].coords.endTime = currTime;
									}
									else {
										this.annotations[i].coords.endTime = this.annotations[i].coords.startTime;
										this.annotations[i].coords.startTime = currTime;
										this.handleHeldDown = (i + "-bottomLeft");
									}
								}
								if (this.khzInBounds(currFreq)) {
									if (currFreq <= this.annotations[i].coords.maxFreq) {
										this.annotations[i].coords.minFreq = currFreq;
									}
									else {
										this.annotations[i].coords.minFreq = this.annotations[i].coords.maxFreq;
										this.annotations[i].coords.maxFreq = currFreq;
										this.handleHeldDown = (i + "-topRight");
									}
								}
								break;
						}
						// this.initialClick.x = x;
						this.initialClick.y = y;
						this.annotations[i].edited = true;
						this.annotationToSave = i;
					}
				}
				this.$forceUpdate();
			}
		},
		constrainBounds(value) {
			if (value > 1) {
				return 1;
			} else if (value < 0) {
				return 0;
			} else {
				return value;
			}
		},
		xInBounds(x) {
			return (x >= Math.max(0, 0.5 - this.offset) && x <= Math.min(1, 0.5 - (this.offset - this.totalOffset)));
		},
		tInBounds(t) {
			const duration = (this.totalOffset * this.width) / (this.zoom * this.basePxPerSec);
			return (t >= 0 && t <= duration);
		},
		khzInBounds(khz) {
			return (khz >= 0 && khz <= this.maxFrequency);
		},
		convertProportionalXToSeconds(x, xOffset, xZoom) {
			// s = px * (1/(px/s))
			const drawnAtTime = (xOffset * this.width) / (this.basePxPerSec * xZoom);
			// cursor is always at time = 0.5
			const tFromCursor = ((x - 0.5) * this.width) / (this.basePxPerSec * xZoom);
			return (drawnAtTime + tFromCursor);
		},
		convertSecondsToProportion(t, offset, zoom) {
			const pxFromStart = t * (this.basePxPerSec * zoom);
			const pxCursor = offset * this.width;
			return ((pxFromStart - pxCursor)/this.width) + 0.5;
		},
		computeDisplay(annotation, side) {
			switch (side) {
				case 'top':
					return (1 - (annotation.coords.maxFreq / this.maxFrequency)) * 100 + '%';
				case 'bottom':
					return (annotation.coords.minFreq / this.maxFrequency) * 100 + '%';
				case 'left':
					return (this.convertSecondsToProportion(annotation.coords.startTime, this.offset, this.zoom)) * 100 + '%';
				case 'right':
					return (1 - this.convertSecondsToProportion(annotation.coords.endTime, this.offset, this.zoom)) * 100 + '%';
			}
		},
		rightClick(e) {
			if (this.enableAnnotating && this.annotations.length && e.which === 3) {
				e.preventDefault();
				const index = this.annotations.length;
				// find index of the last undeleted annotation of the selected category
				let refIndex = null;
				
				for (let i = this.annotations.length - 1; i >= 0; i--) {
					if (this.annotations[i].categoryId === this.activeAnnotation.categoryId && !this.annotations[i].deleted) {
						refIndex = i;
						break; // exit loop once the reference annotation is found
					}
				}
				if (refIndex != null) {
					// cloning an annotation
					const anno = JSON.parse(JSON.stringify(this.annotations[refIndex]));
					if (anno.hasOwnProperty('id')) {
						delete anno.id;
					}
					if (anno.hasOwnProperty('annotationId')) {
						delete anno.annotationId;
					}
					anno.new = true;
					anno.edited = false;
					anno.deleted = false;
					anno.hidden = false;
					this.annotations.push(anno);
					const el = this.$el.getBoundingClientRect();
					// keep cloned box at cursor x position
					const x = (e.clientX - el.left) / el.width;
					const currTime = this.convertProportionalXToSeconds(x, this.offset, this.zoom);
					const duration = this.annotations[index].coords.endTime - this.annotations[index].coords.startTime;
					// make sure cloned box would be in bounds
					if (this.tInBounds(currTime)) {
						this.annotations[index].coords.startTime = currTime;
						this.annotations[index].coords.endTime = Math.min(this.convertProportionalXToSeconds(0.5, this.totalOffset, this.zoom), (currTime + duration));
					}

					this.annotationToSave = index;
					this.saveAnnotations();
				}
			}
		},
		saveAnnotations() {
			// keeping focus is probably not necessary, but without it, focus can be elsewhere unexpectedly
			this.$el.focus();
			// emit all annotations
			//this.$emit('annotations-changed', this.annotations);
			// emit only the new or changed annotation
			this.$emit('annotation-changed', {
				index: this.annotationToSave,
				data: this.annotations,
			});
		},
	},
};
</script>

<style lang="scss">
.AudioAnnotations {
	position: absolute;
	height: 100%;
	width: 100%;
	top: 0;
	user-select: none;
	outline: 0;
	.Annotation-crosshair--x {
		position: absolute;
		left: 0;
		height: 1px;
		width: 100%;
		background-color: rgba(128, 128, 128, 0.4);
		pointer-events: none;
	}
	.Annotation-crosshair--y {
		position: absolute;
		top: 0;
		width: 1px;
		height: 100%;
		background-color: rgba(128, 128, 128, 0.4);
		pointer-events: none;
	}

	// annotation box
	.Annotation {
		position: absolute;
		// default color, used if not sent in props
		border: 2px solid #ff7f00;
		color: #ff7f00;
		pointer-events: none;
		.Annotation-handle {
			border: 1px solid $color-black;
			background-color: $color-white;
			height: 12px;
			width: 12px;
			position: absolute;
			pointer-events: all;
			// in case it overflows
			z-index: 1;
			&--topLeft {
				left: -6px;
				top: -6px;
				cursor: nwse-resize;
			}
			&--topRight {
				right: -6px;
				top: -6px;
				cursor: nesw-resize;
			}
			&--bottomLeft {
				left: -6px;
				bottom: -6px;
				cursor: nesw-resize;
			}
			&--bottomRight {
				right: -6px;
				bottom: -6px;
				cursor: nwse-resize;
			}
			&--middle {
				top: 50%;
				left: 50%;
				transform: translate(-50%, -50%);
				cursor: move;
				height: 14px;
				width: 14px;
				font-size: 10px;
				line-height: 1.2;
				text-align: center;
				font-weight: bold;
				color: $color-text;
			}
		}
		&.hidden,
		&.deleted {
			display: none;
		}
	}
	.Annotation-label {
		position: absolute;
		bottom: calc(100% + 2px);
		left: -2px;
		pointer-events: none;
		color: $color-text;
		padding: 1px $space-xs 2px;
		@include text(1);
		background-color: $color-overlay-white;
		border-top-left-radius: $radius-sm;
		border-top-right-radius: $radius-sm;
		border: 1px solid $color-neutral-3;
		border-bottom: 0;
		white-space: nowrap;
		color: $color-text;
		// in case it overflows the top
		z-index: 1;
	}
}
.AudioAnnotations--alternateHandles {
	// annotation box
	.Annotation {
		.Annotation-handle {
			border: 0;
			background-color: transparent;
			&--topLeft {
				&:after {
					content: '';
					position: absolute;
					height: 12px;
					width: 12px;
					top: 2px;
					left: 2px;
					border-left: 2px solid currentColor;
					border-top: 2px solid currentColor;
				}
			}
			&--topRight {
				&:after {
					content: '';
					position: absolute;
					height: 12px;
					width: 12px;
					top: 2px;
					right: 2px;
					border-right: 2px solid currentColor;
					border-top: 2px solid currentColor;
				}
			}
			&--bottomLeft {
				&:after {
					content: '';
					position: absolute;
					height: 12px;
					width: 12px;
					bottom: 2px;
					left: 2px;
					border-left: 2px solid currentColor;
					border-bottom: 2px solid currentColor;
				}
			}
			&--bottomRight {
				&:after {
					content: '';
					position: absolute;
					height: 12px;
					width: 12px;
					bottom: 2px;
					right: 2px;
					border-right: 2px solid currentColor;
					border-bottom: 2px solid currentColor;
				}
			}
		}
	}
	.Annotation-label {
		pointer-events: all;
		cursor: move;
	}
}
</style>
